// Teacher-side: capture the classroom slide area (boardRef) as a video track
// and publish it to LiveKit under Track.Source.ScreenShare. The hidden egress
// bot that records the lesson subscribes to this track and composes it into
// the recorded MP4, so the recording shows the slide + all interactive state
// exactly as the teacher sees it.
//
// Pipeline: interval-paced html2canvas(boardRef) → draw to persistent canvas →
// canvas.captureStream(0) + manual requestFrame → MediaStreamTrack →
// localParticipant.publishTrack(…, { source: ScreenShare }).
//
// Isolation: if anything in the capture pipeline fails, we log and bail —
// the teacher's own view is never affected. The publisher mounts only when
// a boardRef is available and only for teachers.

import { useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { Track, RoomEvent } from 'livekit-client';

// 2 fps is enough for slide content (static most of the time, occasional slide
// change / drag). Keeps CPU on teacher's browser low (~5-10%).
const CAPTURE_INTERVAL_MS = 500;
// Target canvas size — matches the slide reference. Larger = sharper recording
// but more CPU + bandwidth. 1280x720 is a good trade for the egress MP4.
const CAPTURE_WIDTH = 1280;
const CAPTURE_HEIGHT = 720;

export default function SlideCapturePublisher({ boardRef, enabled = true }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  // Keep refs so cleanup can tear down without needing closure values.
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const captureInFlightRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!room || !localParticipant) return undefined;
    if (!boardRef) return undefined;

    let cancelled = false;
    let onConnected = null;

    const captureOnce = async () => {
      if (cancelled) return;
      if (captureInFlightRef.current) return;
      const el = boardRef.current;
      const ctx = ctxRef.current;
      const track = trackRef.current;
      if (!el || !ctx || !track) return;
      captureInFlightRef.current = true;
      try {
        const snapshot = await html2canvas(el, {
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
          // foreignObjectRendering is faster but breaks on Safari — leave off.
          foreignObjectRendering: false,
          scale: 1,
        });
        if (cancelled) return;
        const srcW = snapshot.width;
        const srcH = snapshot.height;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);
        if (srcW > 0 && srcH > 0) {
          // Fit the snapshot into the capture canvas preserving aspect ratio.
          const scale = Math.min(CAPTURE_WIDTH / srcW, CAPTURE_HEIGHT / srcH);
          const dw = srcW * scale;
          const dh = srcH * scale;
          const dx = (CAPTURE_WIDTH - dw) / 2;
          const dy = (CAPTURE_HEIGHT - dh) / 2;
          ctx.drawImage(snapshot, dx, dy, dw, dh);
        }
        // Manual-mode captureStream: tell the track a new frame is ready.
        if (typeof track.requestFrame === 'function') {
          track.requestFrame();
        }
      } catch (err) {
        // Transient html2canvas failures (e.g. tainted canvas from an un-CORSed
        // image) shouldn't kill the loop — the next tick will try again.
        if (!cancelled) {
          console.debug('[slide-capture] snapshot skipped:', err?.message || err);
        }
      } finally {
        captureInFlightRef.current = false;
      }
    };

    const start = () => {
      if (cancelled) return;
      if (trackRef.current) return; // already started

      const canvas = document.createElement('canvas');
      canvas.width = CAPTURE_WIDTH;
      canvas.height = CAPTURE_HEIGHT;
      const ctx = canvas.getContext('2d');
      // Prime the canvas so captureStream has a frame to emit before the first
      // html2canvas call completes.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);
      canvasRef.current = canvas;
      ctxRef.current = ctx;

      let stream;
      let track;
      try {
        stream = canvas.captureStream(0); // 0 = manual; we requestFrame per draw
        track = stream.getVideoTracks()[0];
        if (!track) throw new Error('captureStream produced no video track');
      } catch (err) {
        console.warn('[slide-capture] captureStream failed:', err);
        return;
      }
      streamRef.current = stream;
      trackRef.current = track;

      (async () => {
        try {
          // Idempotent: don't double-publish if the hook re-runs for any reason.
          if (localParticipant.getTrackPublication(Track.Source.ScreenShare)) return;
          await localParticipant.publishTrack(track, {
            source: Track.Source.ScreenShare,
            name: 'classroom-slide',
          });
        } catch (err) {
          console.warn('[slide-capture] publishTrack failed:', err);
        }
      })();

      // Kick off immediately so the recording doesn't miss the first frames.
      captureOnce();
      intervalRef.current = setInterval(captureOnce, CAPTURE_INTERVAL_MS);
    };

    if (room.state === 'connected') {
      start();
    } else {
      onConnected = () => start();
      room.once(RoomEvent.Connected, onConnected);
    }

    return () => {
      cancelled = true;
      if (onConnected) {
        try { room.off(RoomEvent.Connected, onConnected); } catch (_) {}
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Unpublish + stop so we don't leak tracks or hog bandwidth after unmount.
      if (trackRef.current && localParticipant) {
        try {
          const pub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
          if (pub?.track) {
            localParticipant.unpublishTrack(pub.track, true);
          }
        } catch (err) {
          console.debug('[slide-capture] unpublish on cleanup failed:', err?.message || err);
        }
        try { trackRef.current.stop(); } catch (_) {}
      }
      try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch (_) {}
      trackRef.current = null;
      streamRef.current = null;
      canvasRef.current = null;
      ctxRef.current = null;
    };
  }, [room, localParticipant, boardRef, enabled]);

  return null;
}
